import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { APP_STORE_URL } from '../StoreButtons';
import neaLogo from '../../assets/high_quality_logo/nea_black.png';

// Same menu on every page. The first three entries are sections of the home page.
const NAV = [
  { label: 'Fonctionnalités', hash: '#fonctionnalites' },
  { label: 'Sécurité', hash: '#securite' },
  { label: 'Tarif', hash: '#tarif' },
  { label: 'À propos', to: '/about' },
  { label: 'Support', to: '/support' },
];

const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 1rem 2rem;
  background: rgba(251, 251, 249, 0.88);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--line);

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }
`;

const HeaderInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.8;
  }
`;

const LogoImg = styled.img.attrs({ alt: 'nea' })`
  height: 1.75rem;
  width: auto;
  display: block;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 900px) {
    gap: 0.75rem;
  }
`;

const Links = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  a {
    color: var(--text-lo);
    text-decoration: none;
    font-family: var(--fontR);
    font-size: 0.9375rem;
    font-weight: 500;
    white-space: nowrap;
    transition: color 150ms ease;
  }

  a:hover,
  a.active {
    color: var(--text-hi);
  }

  /* Below 900px the links move into the dropdown opened by the menu button */
  @media (max-width: 900px) {
    display: ${(props) => (props.$open ? 'flex' : 'none')};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    padding: 0.5rem 1rem 1rem;
    background: var(--paper);
    border-bottom: 1px solid var(--line);

    a {
      padding: 0.875rem 0.25rem;
      font-size: 1.0625rem;
      color: var(--text-hi);
      border-bottom: 1px solid var(--line);
    }

    a:last-child {
      border-bottom: none;
    }
  }
`;

const CTAButton = styled.a`
  background: var(--ink-900);
  color: var(--text-inv);
  padding: 0.625rem 1.25rem;
  border-radius: 9999px;
  font-family: var(--fontR);
  font-size: 0.9rem;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  transition: background 150ms ease, transform 150ms ease;

  &:hover {
    background: var(--ink-700);
    transform: translateY(-1px);
  }
`;

const MenuButton = styled.button`
  display: none;

  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 5px;
    width: 40px;
    height: 40px;
    padding: 0 9px;
    border: 1px solid var(--line);
    border-radius: 50%;
    background: transparent;
    cursor: pointer;
  }

  span {
    display: block;
    height: 1.5px;
    border-radius: 1px;
    background: var(--text-hi);
    transition: transform 150ms ease, opacity 150ms ease;
  }

  &[aria-expanded='true'] span:nth-child(1) {
    transform: translateY(6.5px) rotate(45deg);
  }

  &[aria-expanded='true'] span:nth-child(2) {
    opacity: 0;
  }

  &[aria-expanded='true'] span:nth-child(3) {
    transform: translateY(-6.5px) rotate(-45deg);
  }
`;

const Header = () => {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [open, setOpen] = useState(false);

  // Close the mobile menu whenever the page or the section changes
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  return (
    <HeaderWrapper>
      <HeaderInner>
        <Logo to="/"><LogoImg src={neaLogo} /></Logo>
        <Nav aria-label="Navigation principale">
          <Links id="main-menu" $open={open}>
            {NAV.map((item) =>
              item.hash && isHome ? (
                // Already on the home page: a native anchor scrolls every time, even to the current hash
                <a key={item.label} href={item.hash} onClick={() => setOpen(false)}>
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.label}
                  to={item.hash ? `/${item.hash}` : item.to}
                  className={item.to === location.pathname ? 'active' : ''}
                >
                  {item.label}
                </Link>
              )
            )}
          </Links>
          <CTAButton
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-store="app-store"
            data-position="nav"
          >
            Télécharger
          </CTAButton>
          <MenuButton
            type="button"
            aria-label={open ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={open}
            aria-controls="main-menu"
            onClick={() => setOpen((value) => !value)}
          >
            <span />
            <span />
            <span />
          </MenuButton>
        </Nav>
      </HeaderInner>
    </HeaderWrapper>
  );
};

export default Header;
